import { apiService } from './api';
import type { PaginatedResponse } from '../types';

export type CouponDuration = 'FIRST_PAYMENT' | 'EVERY_PAYMENT';
export type ExistingCustomers = 'KEEP' | 'REMOVE';
export interface CurrencyCommissionTotals {
  earned: number;
  pending: number;
  paid: number;
  currency: string;
}
export interface CommissionTotals {
  earned: number | null;
  pending: number | null;
  paid: number | null;
  currency: string | null;
  byCurrency: CurrencyCommissionTotals[];
}
export interface Reseller {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  companyName: string | null;
  isActive: boolean;
  couponCount: number;
  customerCount: number;
  currentCustomerCount: number;
  totals: CommissionTotals;
}
export interface ResellerData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  companyName: string;
  email?: string;
  password?: string;
  isActive?: boolean;
  existingCustomers?: ExistingCustomers;
}
export interface CouponData {
  code: string;
  discountPercent: number;
  discountScope: 'PLAN_ONLY' | 'WHOLE_PAYMENT';
  discountDuration: CouponDuration;
  commissionPercent: number;
  commissionDuration: CouponDuration;
  allowExistingCustomers: boolean;
  appliesToOfflinePayments: boolean;
  expiresAt: string | null;
  maxUses: number | null;
  isActive: boolean;
  existingCustomers?: ExistingCustomers;
}
export interface Coupon extends CouponData {
  expiryDate: string | null;
  id: string;
  resellerId: string;
  uses: number;
  currentCustomers: number;
}
export interface Commission {
  id: string;
  businessName: string;
  paymentType: string;
  paymentDate: string;
  amountPaid: number;
  commissionPercent: number;
  commissionAmount: number;
  currency: string;
  status: 'PENDING' | 'PAID';
  paidAt: string | null;
  paidReference: string | null;
}
export interface CommissionQuery {
  status?: 'PENDING' | 'PAID';
  page?: number;
  limit?: number;
}
export const commissionQuery = (query: CommissionQuery = {}) => {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  return params.size ? `?${params}` : '';
};
export const resellersService = {
  getAll: (page = 1, limit = 20, search = '') =>
    apiService.get<PaginatedResponse<Reseller>>(
      `/admin/resellers?${new URLSearchParams({ page: String(page), limit: String(limit), ...(search && { search }) })}`,
    ),
  create: (data: ResellerData) => apiService.post<Reseller>('/admin/resellers', data),
  update: (id: string, data: Partial<ResellerData>) =>
    apiService.patch<Reseller & { affectedCustomers?: number }>(`/admin/resellers/${id}`, data),
  resetPassword: (id: string, password: string) =>
    apiService.post<void>(`/admin/resellers/${id}/reset-password`, { password }),
  delete: (id: string) => apiService.delete<void>(`/admin/resellers/${id}`),
  getCoupons: (id: string) => apiService.get<Coupon[]>(`/admin/resellers/${id}/coupons`),
  createCoupon: (id: string, data: CouponData) => apiService.post<Coupon>(`/admin/resellers/${id}/coupons`, data),
  updateCoupon: (id: string, couponId: string, data: Partial<CouponData>) =>
    apiService.patch<Coupon & { affectedCustomers?: number }>(`/admin/resellers/${id}/coupons/${couponId}`, data),
  deleteCoupon: (id: string, couponId: string) => apiService.delete<void>(`/admin/resellers/${id}/coupons/${couponId}`),
  getCommissions: (id: string, query?: CommissionQuery) =>
    apiService.get<PaginatedResponse<Commission>>(`/admin/resellers/${id}/commissions${commissionQuery(query)}`),
  markPaid: (id: string, commissionIds: string[], reference?: string, note?: string) =>
    apiService.post<{ markedPaid: number }>(`/admin/resellers/${id}/commissions/mark-paid`, {
      commissionIds,
      ...(reference && { reference }),
      ...(note && { note }),
    }),
};
